import { useCallback, useMemo } from 'react';
import {
  FileDisplay,
  FileDisplayFileObject,
} from '../../components/FileDisplay';
import { FormState } from '../../types/core/form-state.types';
import { FormSchemaConfig } from '../../types/form/schema.types';
import { Field } from '@tw/tw-component-library';
import { useFormContext } from '../../../../../contexts/useFormContext';
import {
  extractBlobUrlsFromFileValue,
  getBlobUrlFromFileObject,
  mergePendingDocumentDeletions,
} from '../../utils/documentDeletion';
import { DisabledStateManager } from '../types';
import {
  validateTagPicker,
  validateRequired,
  validateAmount,
  validateAccountNumber,
  validateSortCode,
  validateIban,
  validateBicSwift,
  BANKING_FIELD_PLACEHOLDERS,
} from '../Utils';

/**
 * Dubai Payment From Office Account - Payment Details Section Schema
 *
 * This schema uses a centralized disabled state management system to ensure
 * consistent behavior across all form fields, similar to the Payment Details schema.
 */
export const useDubaiPaymentFromOfficeAccountPaymentDetailsSchema = (
  formState: FormState,
): FormSchemaConfig => {
  const { updateFormData } = useFormContext();

  const invoiceUploadValue =
    formState.data?.invoiceUpload ?? formState.data?.invoice_upload;

  const handleInvoiceFilesChange = useCallback(
    (value: string, meta?: { removed: FileDisplayFileObject[] }) => {
      const removedUrls = (meta?.removed || [])
        .map((file) => getBlobUrlFromFileObject(file))
        .filter((url): url is string => Boolean(url));

      // If clearing the whole field and removed metadata had no URLs, fall back
      // to extracting URLs from the current stored value before it is cleared.
      const urlsFromCurrentValue =
        value === '' && removedUrls.length === 0
          ? extractBlobUrlsFromFileValue(
              formState.data?.invoiceUpload ?? formState.data?.invoice_upload
            )
          : [];

      updateFormData({
        invoiceUpload: value,
        invoice_upload: value,
        pendingDocumentDeletions: mergePendingDocumentDeletions(
          formState.data?.pendingDocumentDeletions,
          [...removedUrls, ...urlsFromCurrentValue]
        ),
      });
    },
    [
      formState.data?.invoiceUpload,
      formState.data?.invoice_upload,
      formState.data?.pendingDocumentDeletions,
      updateFormData,
    ]
  );

  // ============= Centralized Disabled State Management =============
  const isEditAllowed = Array.isArray(formState.data?.userAssignedSections)
    ? (formState.data?.userAssignedSections as string[]).includes(
        'dubai_payment_from_office_account_payment_details',
      )
    : false;

  const disabledState = useMemo<DisabledStateManager>(() => {
    const isFormCompleted =
      formState.completedSections?.includes(
        'dubai_payment_from_office_account_payment_details',
      ) || false;
    const isFormRejected = formState.isRejected || false;

    return {
      isFormCompleted,

      isFieldDisabled: (
        fieldType: 'readonly' | 'editable' | 'conditional' = 'editable',
      ) => {
        switch (fieldType) {
          case 'readonly':
            return true;
          case 'conditional':
            return (isFormCompleted && !isFormRejected) || !isEditAllowed;
          case 'editable':
          default:
            return (isFormCompleted && !isFormRejected) || !isEditAllowed;
        }
      },

      isTableDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isPaymentTotalDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isTagPickerDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isFileUploadDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isTextAreaDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isRadioDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isSelectDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
    };
  }, [formState.completedSections, isEditAllowed, formState.isRejected]);

  const formSchema = useMemo<FormSchemaConfig>(
    () => ({
      sections: [
        {
          isGrouped: true,
          isGroupedAccordion: true,
          sections: [
            {
              title: { text: 'Selection' },
              layout: 'side-by-side',
              fields: [
                {
                  type: 'select',
                  name: 'paymentType',
                  label: 'Type of payment',
                  required: true,
                  disabled: disabledState.isFieldDisabled('conditional'),
                  validate: (value: unknown) =>
                    validateRequired(value, 'Type of payment'),
                  options: [
                    {
                      value: '1',
                      label: 'Disbursement Payment',
                      disabled: false,
                    },
                    {
                      value: '2',
                      label: 'Business Development/Budget payment',
                      disabled: false,
                    },
                  ],
                },
              ],
              extras: {
                grouped: true,
                evenSpacing: true,
                defaultExpanded: true,
              },
            },
            {
              title: { text: 'Client Details' },
              layout: 'auto',
              dependencies: [
                {
                  type: 'show' as const,
                  logicalOperator: 'AND' as const,
                  conditions: [
                    {
                      field: 'paymentType',
                      operator: 'equals' as const,
                      value: '1',
                    },
                  ],
                },
              ],
              fields: [
                {
                  type: 'clientMatter',
                  name: 'clientMatter',
                  label: 'Client and Matter',
                  required: true,
                  disabled: disabledState.isFieldDisabled('conditional'),
                  extras: {
                    fullWidth: true,
                    clientMatter: {
                      clientNameField: 'clientName',
                      matterNameField: 'matterName',
                      clientUnoField: 'clientUno',
                      matterUnoField: 'matterUno',
                      clientCodeField: 'clientCode',
                      matterCodeField: 'matterCode',
                      selectedClientLabelField: 'selectedClientLabel',
                      clientMatterNumberField: 'clientMatterNumber',
                      office: formState.office,
                      clientLabel: 'Client',
                      matterLabel: 'Matter',
                    },
                  },
                },
                {
                  type: 'text',
                  name: 'selectedClientLabel',
                  label: 'Client Name',
                  placeholder: 'Auto-populated based on selected client',
                  required: true,
                  validate: (value) =>
                    !value || (Array.isArray(value) && value.length === 0)
                      ? 'Client Name is required'
                      : undefined,
                  disabled: disabledState.isFieldDisabled('readonly'),
                },
                {
                  type: 'text',
                  name: 'clientMatterNumber',
                  label: 'Client & Matter Number',
                  placeholder:
                    'Auto-populated based on selected client and matter',
                  required: true,
                  disabled: disabledState.isFieldDisabled('readonly'),
                  validate: (value) =>
                    !value || (Array.isArray(value) && value.length === 0)
                      ? 'Client & Matter Number is required'
                      : undefined,
                },
              ],
              extras: {
                grouped: true,
                evenSpacing: true,
                defaultExpanded: true,
              },
            },
            {
              title: { text: 'Payment Details' },
              layout: 'auto',
              isGrouped: true,
              isGroupedAccordion: false,
              sections: [
                {
                  title: { text: 'Budget Information' },
                  layout: 'auto',
                  dependencies: [
                    {
                      type: 'show' as const,
                      logicalOperator: 'AND' as const,
                      conditions: [
                        {
                          field: 'paymentType',
                          operator: 'equals' as const,
                          value: '2',
                        },
                      ],
                    },
                  ],
                  fields: [
                    {
                      type: 'text',
                      name: 'budgetName',
                      label: 'Budget Name',
                      required: true,
                      disabled: disabledState.isFieldDisabled('conditional'),
                      validate: (value: unknown) =>
                        validateRequired(value, 'Budget Name'),
                    },
                    {
                      type: 'text',
                      name: 'budgetCode',
                      label: 'Budget Code',
                      required: true,
                      disabled: disabledState.isFieldDisabled('conditional'),
                      validate: (value: unknown) =>
                        validateRequired(value, 'Budget Code'),
                    },
                  ],
                  extras: {
                    grouped: true,
                    evenSpacing: true,
                    defaultExpanded: true,
                  },
                },
                {
                  title: { text: 'Payment Details' },
                  layout: 'auto',
                  fields: [
                    {
                      type: 'date',
                      name: 'date',
                      label: 'Date',
                      required: true,
                      disabled: disabledState.isFieldDisabled('conditional'),
                      validate: (value: unknown) =>
                        validateRequired(value, 'Date'),
                    },
                    {
                      type: 'number',
                      name: 'amount',
                      label: 'Amount',
                      required: true,
                      disabled: disabledState.isFieldDisabled('conditional'),
                      validate: validateAmount,
                      extras: {
                        textInput: {
                          formatCurrency: true,
                        },
                      },
                    },
                    {
                      type: 'text',
                      name: 'toWhomPayable',
                      label: 'To Whom Payable',
                      required: true,
                      disabled: disabledState.isFieldDisabled('conditional'),
                      validate: (value: unknown) =>
                        validateRequired(value, 'To Whom Payable'),
                    },
                    {
                      type: 'text',
                      name: 'paymentReference',
                      label: 'Payment Reference',
                      required: true,
                      disabled: disabledState.isFieldDisabled('conditional'),
                      validate: (value: unknown) =>
                        validateRequired(value, 'Payment Reference'),
                    },
                    {
                      type: 'textArea',
                      name: 'ledgerDescription',
                      label: 'Ledger Description',
                      required: true,
                      disabled: disabledState.isTextAreaDisabled(),
                      validate: (value: unknown) =>
                        validateRequired(value, 'Ledger Description'),
                    },
                  ],
                  extras: {
                    grouped: true,
                    evenSpacing: true,
                    defaultExpanded: true,
                  },
                },
                {
                  title: { text: 'Banking Details' },
                  layout: 'auto',
                  fields: [
                    {
                      type: 'text',
                      name: 'receivingBankName',
                      label: 'Receiving Bank Name',
                      required: true,
                      disabled: disabledState.isFieldDisabled('conditional'),
                      validate: (value: unknown) =>
                        validateRequired(value, 'Receiving Bank Name'),
                    },
                    {
                      type: 'text',
                      name: 'bankAddress',
                      label: 'Bank Address',
                      required: true,
                      disabled: disabledState.isFieldDisabled('conditional'),
                      validate: (value: unknown) =>
                        validateRequired(value, 'Bank Address'),
                    },
                    {
                      type: 'accountNumber' as Field['type'],
                      name: 'accountNo',
                      label: 'Account No.',
                      placeholder: BANKING_FIELD_PLACEHOLDERS.accountNumber,
                      required: true,
                      disabled: disabledState.isFieldDisabled('conditional'),
                      validate: (value: unknown) =>
                        validateAccountNumber(value, 'Account No.'),
                    },
                    {
                      type: 'sortCode' as Field['type'],
                      name: 'sortCode',
                      label: 'Sort Code',
                      placeholder: BANKING_FIELD_PLACEHOLDERS.sortCode,
                      required: true,
                      disabled: disabledState.isFieldDisabled('conditional'),
                      validate: (value: unknown) =>
                        validateSortCode(value, 'Sort Code'),
                    },
                    {
                      type: 'iban' as Field['type'],
                      name: 'iban',
                      label: 'IBAN No.',
                      placeholder: BANKING_FIELD_PLACEHOLDERS.iban,
                      required: true,
                      disabled: disabledState.isFieldDisabled('conditional'),
                      validate: (value: unknown) =>
                        validateIban(value, 'IBAN No.'),
                    },
                    {
                      type: 'bicSwift' as Field['type'],
                      name: 'bic',
                      label: 'BIC/SWIFT Code',
                      placeholder: BANKING_FIELD_PLACEHOLDERS.bicSwift,
                      required: true,
                      disabled: disabledState.isFieldDisabled('conditional'),
                      validate: (value: unknown) =>
                        validateBicSwift(value, 'BIC/SWIFT Code'),
                    },
                  ],
                  extras: {
                    grouped: true,
                    evenSpacing: true,
                    defaultExpanded: true,
                  },
                },
                {
                  title: { text: 'Invoice & Documentation' },
                  layout: 'single',
                  fields: [
                    {
                      type: 'radio',
                      name: 'vatInvoiceAttached',
                      label: 'VAT invoice attached?',
                      required: true,
                      disabled: disabledState.isRadioDisabled(),
                      validate: (value: unknown) =>
                        !value
                          ? 'Please specify if VAT invoice is attached'
                          : undefined,
                      options: [
                        {
                          value: 'yes',
                          label: 'Yes',
                          disabled: false,
                        },
                        {
                          value: 'no',
                          label: 'No',
                          disabled: false,
                        },
                      ],
                    },
                    // Show existing files when form is completed (read-only)
                    ...(disabledState.isFormCompleted &&
                    !formState.isRejected &&
                    invoiceUploadValue
                      ? [
                          {
                            type: 'customComponent' as const,
                            customComponent: (
                              <FileDisplay
                                fileName={
                                  invoiceUploadValue as string | File | undefined
                                }
                                workflowId={formState.data?.id}
                                workflowType="dubai-payment-from-office-account"
                              />
                            ),
                            name: 'invoiceUpload_display',
                            label: 'Attached Invoice:',
                          },
                        ]
                      : []),
                    // Show existing files when rejected (allow remove + still allow uploads/replacement)
                    ...(formState.isRejected && invoiceUploadValue
                      ? [
                          {
                            type: 'customComponent' as const,
                            customComponent: (
                              <FileDisplay
                                fileName={
                                  invoiceUploadValue as string | File | undefined
                                }
                                workflowId={formState.data?.id}
                                workflowType="dubai-payment-from-office-account"
                                allowRemove
                                onFilesChange={handleInvoiceFilesChange}
                              />
                            ),
                            name: 'invoiceUpload_display_rejected',
                            label: 'Previously Uploaded Invoice:',
                            dependencies: [
                              {
                                type: 'show' as const,
                                logicalOperator: 'AND' as const,
                                conditions: [
                                  {
                                    field: 'vatInvoiceAttached',
                                    operator: 'equals' as const,
                                    value: 'yes',
                                  },
                                ],
                              },
                            ],
                          },
                        ]
                      : []),
                    // Show file upload unless form is completed and not rejected
                    ...(!disabledState.isFormCompleted || formState.isRejected
                      ? [
                          {
                            type: 'file' as const,
                            name: 'invoiceUpload',
                            label: formState.isRejected
                              ? 'Upload Additional / Replacement Invoice (if needed)'
                              : 'Attach Invoice (if available)',
                            tooltip: 'Document Viewer only supports: .pdf',
                            extras: {
                              file: {
                                multiple: true,
                              },
                            } as Field['extras'],
                            disabled: disabledState.isFileUploadDisabled(),
                            required: false,
                            dependencies: [
                              {
                                type: 'show' as const,
                                logicalOperator: 'AND' as const,
                                conditions: [
                                  {
                                    field: 'vatInvoiceAttached',
                                    operator: 'equals' as const,
                                    value: 'yes',
                                  },
                                ],
                              },
                            ],
                          },
                        ]
                      : []),
                  ],
                  extras: {
                    grouped: true,
                    evenSpacing: true,
                    defaultExpanded: true,
                  },
                },
              ],
              extras: {
                grouped: true,
                evenSpacing: true,
                defaultExpanded: true,
              },
            },
            {
              title: { text: 'Approvers' },
              layout: 'auto',
              fields: [
                {
                  type: 'tagPicker',
                  name: 'approver',
                  label: 'Approver',
                  required: true,
                  disabled: disabledState.isTagPickerDisabled(),
                  validate: (value: unknown) =>
                    validateTagPicker(value, 'Approver'),
                  extras: {
                    tagPicker: {
                      minSearchLength: 4,
                      maxTags: 1,
                      createNewTags: false,
                    },
                  },
                },
              ],
              extras: {
                grouped: true,
                evenSpacing: true,
                defaultExpanded: true,
              },
            },
          ],
        },
      ],
    }),
    [
      disabledState,
      invoiceUploadValue,
      formState.data?.id,
      formState.isRejected,
      formState.office,
      handleInvoiceFilesChange,
    ],
  );

  // The returned object is the complete form schema, ready for rendering
  return formSchema;
};
